import { normalizeAppOrigin, type JsonValue } from "./protocol";
import type { CredentialClaimNameV1 } from "./credentials";

export const LEGACY_NOTE_TEMPLATE_ID = "vibe.content.note.v1";
export const NOTE_TEMPLATE_ID = "vibe.content.note.v2";
export const NOTE_TEMPLATE_SCHEMA_SHA256 = "3cd2e80c9662a4d0a2c60bb4e836653bafadfc16b38e4c9f864bfdd14dda69e7";
export const POST_TEMPLATE_ID = "vibe.content.post.v1";
export const POST_TEMPLATE_SCHEMA_SHA256 = "f20b14e6707388e0f05a48b32cf950fffcfc714fa6c632b2ea5047b767fa237e";

export interface ContentTemplateManifest {
  id: string;
  name: string;
  schema: Record<string, JsonValue>;
  schemaSha256: string;
  searchFields: string[];
}

export type AppOnboardingFieldType = "text" | "email" | "url" | "textarea" | "select";

export interface AppOnboardingFieldOption {
  value: string;
  label: string;
}

export interface AppOnboardingFieldManifest {
  id: string;
  label: string;
  type: AppOnboardingFieldType;
  required: boolean;
  description?: string;
  placeholder?: string;
  maxLength?: number;
  options?: AppOnboardingFieldOption[];
}

export interface AppOnboardingManifest {
  title: string;
  description: string;
  fields: AppOnboardingFieldManifest[];
}

export interface CredentialRequirementManifest {
  id: string;
  type: string;
  claims: CredentialClaimNameV1[];
  required: boolean;
  label?: string;
  purpose: string;
  acceptedIssuers?: string[];
}

export interface VibeAppManifest {
  version: 1;
  id: string;
  name: string;
  permissions: string[];
  contentTemplates: ContentTemplateManifest[];
  presentation?: {
    tagline: string;
    description: string;
    logo?: string;
    heroImage?: string;
    locale?: string;
    accentColor?: string;
  };
  onboarding?: AppOnboardingManifest;
  credentialRequirements?: CredentialRequirementManifest[];
}

export interface PublicVibeAppManifest extends Omit<VibeAppManifest, "contentTemplates"> {
  contentTemplates: Array<Omit<ContentTemplateManifest, "schema"> & { schema: string }>;
}

export interface ConfiguredAppManifest {
  origin: string;
  manifest: VibeAppManifest;
}

export const NOTE_TEMPLATE_SCHEMA: Record<string, JsonValue> = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  $id: NOTE_TEMPLATE_ID,
  type: "object",
  properties: {
    body: { type: "string" },
  },
  required: ["body"],
  additionalProperties: false,
};

export const VIBE_NOTES_MANIFEST: VibeAppManifest = {
  version: 1,
  id: "vibe-notes",
  name: "VibeNotes",
  presentation: {
    tagline: "Notes that stay with you.",
    description: "Capture ideas from any device in a quiet, distraction-free notebook backed by your Vibe node.",
    logo: "/assets/vibe-notes.png",
    heroImage: "/assets/vibe-notes-showcase.jpg",
  },
  permissions: [
    "profile:read",
    `content.read:${NOTE_TEMPLATE_ID}`,
    `content.write:${NOTE_TEMPLATE_ID}`,
    "collections.read",
    "collections.write",
    `semantic.search:${NOTE_TEMPLATE_ID}`,
    "service.use:openai.embeddings",
  ],
  contentTemplates: [
    {
      id: NOTE_TEMPLATE_ID,
      name: "Note",
      schema: NOTE_TEMPLATE_SCHEMA,
      schemaSha256: NOTE_TEMPLATE_SCHEMA_SHA256,
      searchFields: ["body"],
    },
  ],
};

export const VIBE_NOTES_PUBLIC_MANIFEST: PublicVibeAppManifest = {
  ...VIBE_NOTES_MANIFEST,
  contentTemplates: VIBE_NOTES_MANIFEST.contentTemplates.map(({ schema: _schema, ...template }) => ({
    ...template,
    schema: "/schemas/note-v2.schema.json",
  })),
};

export function configureVibeNotes(origin: string): ConfiguredAppManifest {
  return { origin: normalizeAppOrigin(origin), manifest: VIBE_NOTES_MANIFEST };
}

export const CREDENTIAL_DEMO_MANIFEST: VibeAppManifest = {
  version: 1,
  id: "credential-gate-demo",
  name: "Credential Gate",
  presentation: {
    tagline: "Sign in with the details you choose.",
    description: "Credential Gate asks for a verified email and lets you share a name, all during sign-in.",
  },
  permissions: ["profile:read", "credentials.present"],
  contentTemplates: [],
  credentialRequirements: [
    {
      id: "verified_email",
      type: "vibe.credential.verified-email.v1",
      claims: ["email"],
      required: true,
      label: "Verified email",
      purpose: "Credential Gate uses this to confirm you control an email address.",
    },
    {
      id: "display_name",
      type: "vibe.credential.self-issued.v1",
      claims: ["name"],
      required: false,
      label: "Name",
      purpose: "Use a name in Credential Gate instead of your DID.",
    },
  ],
};

export function configureCredentialDemo(origin: string): ConfiguredAppManifest {
  return { origin: normalizeAppOrigin(origin), manifest: CREDENTIAL_DEMO_MANIFEST };
}

export const VIBE_CALENDAR_MANIFEST: VibeAppManifest = {
  version: 1,
  id: "vibe-calendar",
  name: "Vibe Calendar",
  presentation: {
    tagline: "Your schedule, copied privately to Vibe.",
    description: "View calendars you explicitly sync through your Vibe Cloud node.",
  },
  permissions: ["profile:read", "calendar.read", "calendar.sync"],
  contentTemplates: [],
};

export function configureVibeCalendar(origin: string): ConfiguredAppManifest {
  return { origin: normalizeAppOrigin(origin), manifest: VIBE_CALENDAR_MANIFEST };
}

export const POST_TEMPLATE_SCHEMA: Record<string, JsonValue> = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  $id: POST_TEMPLATE_ID,
  type: "object",
  properties: {
    text: { type: "string", minLength: 1, maxLength: 500 },
    published: { type: "boolean" },
  },
  required: ["text", "published"],
  additionalProperties: false,
};

export const VIBE_FEED_MANIFEST: VibeAppManifest = {
  version: 1,
  id: "vibe-feed",
  name: "Vibe Feed",
  presentation: {
    tagline: "Your posts, rooted in your Vibe identity.",
    description: "Create short posts, keep drafts private, and publish a canonical public feed under your Vibe identity.",
    logo: "/assets/vibe-feed-logo.png",
  },
  permissions: [
    "profile:read",
    `content.read:${POST_TEMPLATE_ID}`,
    `content.write:${POST_TEMPLATE_ID}`,
    "feed.publish",
  ],
  contentTemplates: [{
    id: POST_TEMPLATE_ID,
    name: "Post",
    schema: POST_TEMPLATE_SCHEMA,
    schemaSha256: POST_TEMPLATE_SCHEMA_SHA256,
    searchFields: ["text"],
  }],
};

export const VIBE_FEED_PUBLIC_MANIFEST: PublicVibeAppManifest = {
  ...VIBE_FEED_MANIFEST,
  contentTemplates: VIBE_FEED_MANIFEST.contentTemplates.map(({ schema: _schema, ...template }) => ({
    ...template,
    schema: "/schemas/post-v1.schema.json",
  })),
};

export function configureVibeFeed(origin: string): ConfiguredAppManifest {
  return { origin: normalizeAppOrigin(origin), manifest: VIBE_FEED_MANIFEST };
}
